import { useCallback, useEffect, useState } from 'react'
import type { DragOffset } from './WorkbenchFloatingBits'

/** Keep this much of the container visible around the bar on every side. */
const MARGIN = 8

/**
 * Clamp a drag offset so the bar stays wholly inside `container`.
 *
 * The offset is relative to wherever CSS put the bar, not an absolute position, so the
 * limits have to be measured from its REST position — and the bar's own rect can't give
 * that, because it already includes the transform being clamped.
 *
 * `el.offsetParent` can: the bars are built as an anchoring wrapper (which owns the
 * `top-12 left-1/2` placement and any responsive flip) around an inner element (which
 * owns only the translate). A translate on the inner element does not move its
 * offsetParent, so the wrapper's rect is the rest position, exactly, with nothing to
 * infer about what React has already committed to the DOM.
 *
 * Exported for the unit test; callers use the hook.
 */
export function clampDragOffset(
  el: HTMLElement,
  container: HTMLElement,
  drag: DragOffset,
): DragOffset {
  const anchor = el.offsetParent
  if (!anchor) return drag

  const rest = anchor.getBoundingClientRect()
  const bounds = container.getBoundingClientRect()
  // Nothing measurable yet (or a container too small to hold the bar at all): leave the
  // offset alone rather than fighting over pixels that don't exist.
  if (rest.width <= 0 || bounds.width <= 0) return drag

  const minDx = bounds.left + MARGIN - rest.left
  const maxDx = bounds.right - MARGIN - rest.right
  const minDy = bounds.top + MARGIN - rest.top
  const maxDy = bounds.bottom - MARGIN - rest.bottom

  return {
    // max(min, max) before the outer min: when the container is narrower than the bar the
    // range inverts, and pinning to the top-left keeps the grip reachable.
    dx: Math.round(Math.min(Math.max(drag.dx, minDx), Math.max(minDx, maxDx))),
    dy: Math.round(Math.min(Math.max(drag.dy, minDy), Math.max(minDy, maxDy))),
  }
}

export interface FloatingBarDrag {
  drag: DragOffset
  /** Clamps before storing, so no caller can place the bar out of reach. */
  setDrag: (d: DragOffset) => void
  reset: () => void
  /** Attach to the element being moved. A callback ref, so a swap re-runs the clamp. */
  setEl: (el: HTMLElement | null) => void
  /** Attach to the box the bar must stay inside. */
  setContainerEl: (el: HTMLElement | null) => void
}

/**
 * A user-chosen offset for a floating bar, clamped to its container.
 *
 * Owned by the workbench rather than by either bar because the same position is shared by
 * two elements that are never mounted together: the walkthrough transport bar and the
 * standalone Canvas | Walkthrough pill. Keeping it here is what makes the cluster stay
 * where the user put it across a mode switch — and across a walkthrough rebuild, which
 * remounts the whole replay subtree on a new key.
 *
 * Deliberately not persisted to storage: no other panel size or toolbar position in the
 * app is, and a stale offset against a differently-sized chart is worse than centred.
 */
export function useFloatingBarDrag(): FloatingBarDrag {
  const [drag, setDragState] = useState<DragOffset>({ dx: 0, dy: 0 })
  const [el, setEl] = useState<HTMLElement | null>(null)
  const [container, setContainerEl] = useState<HTMLElement | null>(null)

  const setDrag = useCallback((d: DragOffset) => {
    setDragState(el && container ? clampDragOffset(el, container, d) : d)
  }, [el, container])

  const reset = useCallback(() => setDragState({ dx: 0, dy: 0 }), [])

  // Re-clamp when the container resizes, so narrowing the chart can never strand the bar
  // (and its grip) outside the visible area with no way to drag it back. Also runs once
  // per element, which covers the handover between the two bars — they are different
  // widths, so an offset legal for one can be out of bounds for the other.
  useEffect(() => {
    if (!el || !container) return

    const reclamp = () => {
      setDragState((prev) => {
        if (prev.dx === 0 && prev.dy === 0) return prev
        const next = clampDragOffset(el, container, prev)
        return next.dx === prev.dx && next.dy === prev.dy ? prev : next
      })
    }

    reclamp()
    const observer = new ResizeObserver(reclamp)
    observer.observe(container)
    return () => observer.disconnect()
  }, [el, container])

  return { drag, setDrag, reset, setEl, setContainerEl }
}

export default useFloatingBarDrag
